import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import WaitlistModal from "../components/WaitlistModal";
import SovereignToolkit from "../components/SovereignToolkit";
import { ShieldCheck, Radio, ArrowRight, AlertOctagon, CheckCircle2, Clock, Lock, Skull } from "lucide-react";
import EigooMark from "../components/EigooMark";
import { BoardLink } from "../lib/boardLinks";

const CONTROLS = [
    { id: "AC-02", family: "Access Control", name: "Privileged account review", status: "pass", score: 98, last: "2d ago" },
    { id: "SC-07", family: "System Comms", name: "Perimeter egress filtering", status: "pass", score: 94, last: "4h ago" },
    { id: "IA-05", family: "Identification", name: "Credential rotation (90d)", status: "warn", score: 71, last: "18d ago" },
    { id: "AU-06", family: "Audit", name: "SIEM correlation health", status: "pass", score: 89, last: "1h ago" },
    { id: "CM-06", family: "Config Mgmt", name: "Baseline configuration drift", status: "fail", score: 42, last: "just now" },
    { id: "IR-04", family: "Incident Resp", name: "Tabletop exercise cadence", status: "warn", score: 68, last: "62d ago" },
];

const STATUS_PILL = {
    pass: "bg-[hsl(153_40%_30%)] text-[hsl(153_50%_80%)]",
    warn: "bg-[hsl(40_60%_28%)] text-[hsl(45_85%_80%)]",
    fail: "bg-[hsl(350_60%_35%)] text-[hsl(0_0%_98%)]",
};

const STATUS_ICON = {
    pass: CheckCircle2,
    warn: Clock,
    fail: AlertOctagon,
};

export default function CyberHome() {
    const { user } = useAuth();
    const [showWaitlist, setShowWaitlist] = useState(false);
    const passPct = Math.round(
        (CONTROLS.filter((c) => c.status === "pass").length / CONTROLS.length) * 100,
    );
    return (
        <div className="min-h-screen bg-background grain-overlay">
            {showWaitlist && (
                <WaitlistModal tenant="cyberops" label="Request a governance audit" onClose={() => setShowWaitlist(false)} />
            )}
            <header className="relative z-10 max-w-[1400px] mx-auto flex items-center justify-between px-6 md:px-10 py-6">
                <div className="flex items-baseline gap-3">
                    <EigooMark size="lg" sub="Tech Ops" testid="cyber-mark" />
                </div>
                <div className="flex items-center gap-3">
                    {user ? (
                        <Link to="/app/dashboard" className="px-5 py-2 rounded-full bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] text-sm font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors" data-testid="cyber-enter-portal">Enter Portal</Link>
                    ) : (
                        <>
                            <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground">Sign In</Link>
                            <BoardLink data-testid="cyber-board-bridge" className="px-5 py-2 rounded-full bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] text-sm font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors">Board the Bridge</BoardLink>
                        </>
                    )}
                </div>
            </header>

            <section className="max-w-[1400px] mx-auto px-6 md:px-10 pt-8 pb-24">
                <div className="fade-up max-w-3xl">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[hsl(40_50%_40%/0.4)] text-[10px] uppercase tracking-[0.3em] text-[hsl(45_60%_70%)] mb-6">
                        <Radio size={10} className="text-[hsl(45_85%_62%)]" /> Governance Command
                    </div>
                    <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter">
                        Posture, audited
                        <span className="block gold-gradient-text mt-2">in real time.</span>
                    </h1>
                    <p className="mt-8 text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed">
                        Continuous control attestation across the estate — SOC 2, ISO 27001, NIST. Each control
                        carries its own uptime, drift window, and attestation trail.
                    </p>

                    {/* Vault crossover CTA — escalates visitors needing adversarial work */}
                    <a
                        href="https://techopssa.eigooinc.net"
                        data-testid="cyber-vault-cta"
                        className="group mt-8 inline-flex items-center gap-3 px-6 py-4 rounded-2xl bg-gradient-to-r from-[hsl(350_55%_18%)] via-[hsl(151_55%_10%)] to-[hsl(40_55%_22%)] border border-[hsl(45_85%_62%/0.5)] hover:border-[hsl(45_85%_82%)] transition-all shadow-[0_8px_30px_-12px_hsl(40_70%_45%/0.45)]"
                    >
                        <div className="w-10 h-10 rounded-full bg-[hsl(151_55%_5%)] border border-[hsl(45_85%_62%/0.5)] flex items-center justify-center shrink-0">
                            <Lock size={16} className="text-[hsl(45_85%_68%)]" />
                        </div>
                        <div className="text-left">
                            <div className="text-[10px] uppercase tracking-[0.35em] text-[hsl(45_85%_68%)] flex items-center gap-1.5">
                                <Skull size={10} /> ENTER THE VAULT
                            </div>
                            <div className="text-base font-medium text-foreground mt-0.5">
                                Access Tech Ops SA for Elite Audits
                            </div>
                        </div>
                        <ArrowRight size={18} className="ml-2 text-[hsl(45_85%_68%)] transition-transform group-hover:translate-x-1" />
                    </a>
                </div>

                {/* Posture widget */}
                <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-6 fade-up" style={{ animationDelay: "0.15s" }}>
                    <div className="glass rounded-2xl p-6 lg:col-span-1">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Composite posture</div>
                        <div className="font-heading text-6xl mt-3 gold-gradient-text" data-testid="posture-score">{passPct}%</div>
                        <div className="text-xs text-muted-foreground mt-2">{CONTROLS.filter((c) => c.status === "pass").length} of {CONTROLS.length} controls passing</div>
                        <div className="mt-6 space-y-2 text-xs">
                            <Row label="Passing" value={CONTROLS.filter((c) => c.status === "pass").length} color="hsl(153 50% 65%)" />
                            <Row label="Warning" value={CONTROLS.filter((c) => c.status === "warn").length} color="hsl(45 85% 68%)" />
                            <Row label="Failing" value={CONTROLS.filter((c) => c.status === "fail").length} color="hsl(350 75% 70%)" />
                        </div>
                        <div className="mt-6 flex items-center gap-2 text-xs text-[hsl(45_85%_68%)]">
                            <ShieldCheck size={14} /> SOC2 · ISO27001 · NIST 800-53
                        </div>
                    </div>

                    <div className="glass rounded-2xl p-6 lg:col-span-2">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <div className="font-heading text-2xl">Control attestation feed</div>
                                <div className="text-xs text-muted-foreground">Continuous evidence collection <span className="text-[hsl(45_85%_68%)]">(demo feed)</span></div>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-[hsl(45_85%_68%)]">
                                <span className="w-2 h-2 rounded-full bg-[hsl(45_85%_62%)] animate-pulse" />
                                signal feed active
                            </div>
                        </div>
                        <div className="space-y-2">
                            {CONTROLS.map((c) => {
                                const Icon = STATUS_ICON[c.status];
                                return (
                                    <div key={c.id} className="p-3 rounded-lg bg-[hsl(151_55%_13%)] border border-[hsl(40_40%_25%/0.14)] flex items-center gap-4" data-testid={`control-${c.id}`}>
                                        <Icon size={16} className={c.status === "pass" ? "text-[hsl(153_50%_65%)]" : c.status === "warn" ? "text-[hsl(45_85%_68%)]" : "text-[hsl(350_75%_70%)]"} />
                                        <div className="font-mono text-xs text-muted-foreground w-14">{c.id}</div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm">{c.name}</div>
                                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{c.family}</div>
                                        </div>
                                        <div className="font-mono text-sm w-12 text-right">{c.score}</div>
                                        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_PILL[c.status]}`}>{c.status}</span>
                                        <div className="text-[10px] text-muted-foreground w-16 text-right">{c.last}</div>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="mt-5 p-4 rounded-lg border border-dashed border-[hsl(40_40%_30%/0.3)] text-xs text-muted-foreground">
                            <span className="text-[hsl(45_85%_68%)] font-semibold">Placeholder feed.</span> Pick your framework (SOC 2, ISO 27001, NIST 800-53, CIS) and I'll wire attestation ingestion (Drata, Vanta, or self-hosted evidence).
                        </div>
                    </div>
                </div>

                <div className="mt-10 flex flex-wrap gap-4">
                    <button onClick={() => setShowWaitlist(true)} className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors" data-testid="cyber-cta">
                        <ShieldCheck size={18} /> Begin audit <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                    </button>
                    {user && (
                        <Link to="/app/dashboard" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-[hsl(40_50%_35%/0.4)] text-foreground hover:bg-[hsl(151_55%_15%)] transition-colors">
                            Open portal
                        </Link>
                    )}
                </div>
            </section>

            {/* Sovereign Toolkit · TechOps affiliate signals (Director directive 2026.02) */}
            <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-16">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="md:col-span-2">
                        <h2 className="font-heading text-2xl mb-2">Hardware procurement signals</h2>
                        <p className="text-sm text-muted-foreground max-w-xl leading-relaxed">
                            TechOps clients procure laptops, servers, monitors, and security devices.
                            Every order routed through these rails generates Commander commission under
                            the 60/30/10 covenant.
                        </p>
                    </div>
                    <SovereignToolkit vessel="eigoo_techops" title="TechOps Toolkit" />
                </div>
            </section>

            <footer className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 border-t border-[hsl(40_40%_30%/0.15)] text-xs text-muted-foreground flex flex-wrap items-center justify-between gap-4">
                <div>© EIGOO Tech Ops · Posture, continuously attested.</div>
                <div className="font-mono">techops.eigooinc.net</div>
            </footer>
        </div>
    );
}

function Row({ label, value, color }) {
    return (
        <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-muted-foreground">
                <span className="w-2 h-2 rounded-sm" style={{ background: color }} />
                <span className="uppercase tracking-wider">{label}</span>
            </div>
            <span className="font-mono">{value}</span>
        </div>
    );
}
